import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  createAuthProvider,
  type AuthProvider as IAuthProvider,
  type AuthSession,
  type AuthContextValue,
} from '@forma-studio/auth';

const AuthContext = createContext<AuthContextValue | null>(null);

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '';

let authProvider: IAuthProvider | null = null;

function getAuthProvider(): IAuthProvider | null {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
  if (!authProvider) {
    authProvider = createAuthProvider(SUPABASE_URL, SUPABASE_ANON_KEY, {
      storageKey: 'forma-studio-auth',
      cookieDomain: '.forma.frankriccidesign.com',
    });
  }
  return authProvider;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const provider = getAuthProvider();
    if (!provider) {
      setIsLoading(false);
      return;
    }

    provider.getSession().then((s) => {
      setSession(s);
      setIsLoading(false);
    });

    const unsubscribe = provider.onAuthStateChange((s) => {
      setSession(s);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (session) {
      document.body.classList.add('fs-logged-in');
    } else {
      document.body.classList.remove('fs-logged-in');
    }
  }, [session]);

  const signIn = useCallback(async (email: string, password: string) => {
    const provider = getAuthProvider();
    if (!provider) throw new Error('Auth not configured');
    const s = await provider.signIn(email, password);
    setSession(s);
  }, []);

  const signInWithOtp = useCallback(async (email: string) => {
    const provider = getAuthProvider();
    if (!provider) throw new Error('Auth not configured');
    await provider.signInWithOtp(email);
  }, []);

  const signOut = useCallback(async () => {
    const provider = getAuthProvider();
    if (!provider) return;
    await provider.signOut();
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ session, isLoading, signIn, signInWithOtp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
